import {Utils, FileUtils, StringUtils, UriHelper, lodash} from 'neo-common';
import {ForceToolCommand, ForceToolDefinition, ForceToolSet, ForceToolSetFile, SetCategory, SetDefinition, SetFileDefinition} from './Types';
import {IHashItem} from "neo-common/Engine/Utils/UriHelper";
//import {readLocalJsonFile} from './ForceUtils';

const baseFolder = 'ForceTool';
const fileDefinition = `${baseFolder}/definition.json`;

const windowKeyPrefix = 'NEO_ForceTool_';

const windowKeys = {
    Definition: windowKeyPrefix + 'Definition',
    Current: {
        Category: windowKeyPrefix + 'Current_Category',
        File: windowKeyPrefix + 'Current_File',
        CommandIndex: windowKeyPrefix + 'Current_CommandIndex',
    }
};

const queryStrings = {
    forceToolEnabled: 'force'
};

const queryHashes = {
    category: 'category',
    file: 'file'
};

type FileCategoryDefinition = {
    [SetName: string]: SetFileDefinition;
}

type FileDefinition = {
    Categories: {[CategoryName: string]: FileCategoryDefinition};
}

export class ForceToolApi {
    public static getSampleForceSetFile(): ForceToolSetFile {
        return {
            Name: 'SampleSet',
            Description: 'Sample Force Set Commands File',
            Commands: [
                {
                    Description: 'Command 1',
                    Module: 'base',
                    Action: 'spin',
                    Command: '/S'
                },
                {
                    Description: 'Command 2',
                    Module: 'freespin',
                    Action: 'spin',
                    Command: '/S /S'
                }
            ]
        };
    }

    public static updateCurrentForceSet(newForceSet: ForceToolSet): Promise<boolean> {
        return Promise.resolve()
            .then(() => {
                const categoryName = window[windowKeys.Current.Category] as string;
                const fileName = window[windowKeys.Current.File] as string;
                return !StringUtils.isNullOrEmpty(categoryName) && !StringUtils.isNullOrEmpty(fileName)
                    ? ForceToolApi.getFileSet(categoryName, fileName)
                    : null;
            })
            .then(set => {
                if (set) {
                    if ('File' in newForceSet) {
                        delete newForceSet.File;
                    }

                    lodash.assign(set, newForceSet);
                    return true;
                }

                return false;
            });
    }

    public static clearCurrent(): void {
        ForceToolApi.setCurrentCommandIndex(-1);
        window[windowKeys.Current.Category] = '';
        window[windowKeys.Current.File] = '';
        ForceToolApi.removeUrlHash();
    }

    public static resetCurrentForceSet(): Promise<ForceToolSet> {
        return Promise.resolve()
            .then(() => {
                const categoryName = window[windowKeys.Current.Category] as string;
                const fileName = window[windowKeys.Current.File] as string;
                return !StringUtils.isNullOrEmpty(categoryName) && !StringUtils.isNullOrEmpty(fileName)
                    ? ForceToolApi.getFileSet(categoryName, fileName, true)
                    : null;
            });
    }

    public static getFileSet(categoryName: string, fileName: string, restoreFromFile: boolean = false): Promise<ForceToolSet> {

        function updateCurrent(set: ForceToolSet, categoryName: string, fileName: string) {
            window[windowKeys.Current.Category] = categoryName;
            window[windowKeys.Current.File] = fileName;

            let currentCommandIndex = ForceToolApi.getCurrentCommandIndex();
            if (currentCommandIndex >= set.Commands.length) {
                currentCommandIndex = -1;
                ForceToolApi.setCurrentCommandIndex(currentCommandIndex);
            }

            if (set.Commands.length > 0 && currentCommandIndex === -1) {
                currentCommandIndex = 0;
                ForceToolApi.setCurrentCommandIndex(currentCommandIndex);
            }

            // update current URL hash to include 'category' and 'file' with its values
            const hashGroups = UriHelper.getHashGroups();
            let itemCategory = lodash.find(hashGroups, (item: IHashItem) => item.name === queryHashes.category);
            if (Utils.isNullOrUndefined(itemCategory)) {
                itemCategory = {
                    name: queryHashes.category,
                    value: ''
                };
                hashGroups.push(itemCategory);
            }

            let itemFile = lodash.find(hashGroups, (item: IHashItem) => item.name === queryHashes.file);
            if (Utils.isNullOrUndefined(itemFile)) {
                itemFile = {
                    name: queryHashes.file,
                    value: ''
                };
                hashGroups.push(itemFile);
            }

            itemCategory.value = categoryName;
            itemFile.value = ForceToolApi.extractFileBaseName(fileName);
            UriHelper.updateHash(hashGroups);
        }

        return ForceToolApi.getDefinition()
            .then(definition => {
                const category = lodash.find(definition.Categories, (categ: any) => categ.Name === categoryName);
                if (!Utils.isNullOrUndefined(category)) {
                    const categorySet = lodash.find(category.Sets, (set: any) => set.File === fileName);
                    if (!Utils.isNullOrUndefined(categorySet)) {
                        if (restoreFromFile === true) {
                            categorySet.Set = null;
                        }

                        if (Utils.isNullOrUndefined(categorySet.Set)) {
                            return ForceToolApi.readForceSetFile(fileName)
                                .then(set => {
                                    categorySet.Set = set;
                                    updateCurrent(set, categoryName, fileName);
                                    return set;
                                });
                        }

                        updateCurrent(categorySet.Set, categoryName, fileName);
                        return categorySet.Set;
                    }
                }

                return null;
            });
    }

    public static removeUrlHash(): void {
        const hashGroups = UriHelper.getHashGroups();
        lodash.remove(hashGroups, (item: IHashItem) => item.name === queryHashes.category);
        lodash.remove(hashGroups, (item: IHashItem) => item.name === queryHashes.file);

        if (hashGroups.length === 0) {
            if ('pushState' in history) {
                history.replaceState('', document.title, window.location.pathname + window.location.search);
            } else {
                location.hash = '';
            }
        } else {
            UriHelper.updateHash(hashGroups);
        }
    }

    private static readForceSetFile(fileName: string): Promise<ForceToolSet> {
        //return readLocalJsonFile<ForceToolSet>(UriHelper.setTimeStamp(fileName))
        return readLocalJsonFile<ForceToolSet>(UriHelper.setTimeStamp(fileName))
            .catch(err => {
                if (DEBUG) {
                    console.warn(`Read force tool set from file '${fileName}' failed`, err);
                }

                return null;
            })
            .then(setFile => {
                const fileExist = !Utils.isNullOrUndefined(setFile);

                const result: ForceToolSet = ForceToolApi.isValidForceSetFile(setFile) ? setFile : {
                    File: fileName,
                    Name: '-',
                    Description: fileExist ? 'Invalid file format!' : 'File does not exist',
                    Commands: []
                };

                result.File = fileName;

                return result;
            });
    }

    private static requiredCommandKeys = ['Action', 'Command', 'Module'];

    public static isValidForceSetFile(setFile: ForceToolSet): boolean {
        let valid = !Utils.isNullOrUndefined(setFile) &&
                    !Utils.isNullOrUndefined(setFile.Name) &&
                    !Utils.isNullOrUndefined(setFile.Commands);

        if (valid && lodash.isArray(setFile.Commands) && setFile.Commands.length > 0) {
            valid = lodash.every(setFile.Commands, (cmd: ForceToolCommand) => {
                return ForceToolApi.isValidForceCommand(cmd);
            });
        }
        return valid;
    }

    public static get hasSelectedForceSet(): boolean {
        var categoryName = window[windowKeys.Current.Category];
        var fileName = window[windowKeys.Current.File];
        return !StringUtils.isNullOrEmpty(categoryName) && !StringUtils.isNullOrEmpty(fileName);
    }

    public static isValidForceCommand(command: ForceToolCommand): boolean {
        const validKeys = lodash.intersection(lodash.keys(command), ForceToolApi.requiredCommandKeys);
        return !Utils.isNullOrUndefined(command.Description) &&
               validKeys.length === ForceToolApi.requiredCommandKeys.length;
    }

    public static getCurrentForceSet(): Promise<ForceToolSet> {
        return Promise.resolve()
            .then(() => {
                const categoryName = window[windowKeys.Current.Category] as string;
                const fileName = window[windowKeys.Current.File] as string;
                return !StringUtils.isNullOrEmpty(categoryName) && !StringUtils.isNullOrEmpty(fileName)
                    ? ForceToolApi.getFileSet(categoryName, fileName)
                    : null;
            })
            .then(set => {
                if (!Utils.isNullOrUndefined(set)) {
                    return set;
                }

                const hashGroups = UriHelper.getHashGroups();
                const itemCategory = lodash.find(hashGroups, (item: IHashItem) => item.name === queryHashes.category);
                const itemFile = lodash.find(hashGroups, (item: IHashItem) => item.name === queryHashes.file);
                const category = itemCategory ? itemCategory.value : null;
                let file = itemFile ? itemFile.value : null;
                const validData = !Utils.isNullOrUndefined(category) && !Utils.isNullOrUndefined(file);
                if (validData) {
                    file = ForceToolApi.createFileName(category, file);
                }

                return validData ? ForceToolApi.getFileSet(category, file) : null;
            });
    }

    public static getCurrentCommandIndex(): number {
        let currentCommandIndex = window[windowKeys.Current.CommandIndex] as number;
        if (Utils.isNullOrUndefined(currentCommandIndex)) {
            currentCommandIndex = -1;
            ForceToolApi.setCurrentCommandIndex(currentCommandIndex);
        }

        return currentCommandIndex;
    }

    public static setCurrentCommandIndex(value: number): void {
        window[windowKeys.Current.CommandIndex] = value;
    }

    public static getDefinition(): Promise<ForceToolDefinition> {
        let definition = window[windowKeys.Definition] as ForceToolDefinition;
        if (!Utils.isNullOrUndefined(definition)) {
            return Promise.resolve(definition);
        }

        return ForceToolApi.readDefinition()
            .then(definition => {
                if (!Utils.isNullOrUndefined(definition)) {
                    // cache it in window obj
                    window[windowKeys.Definition] = definition;
                }
                return definition;
            });
    }

    public static getNonBaseFilePath(fileName: string): string {
        if (!Utils.isNullOrUndefined(fileName)) {
            var parts = fileName.split('/');
            if (parts.length > 0) {
                if (parts[0].toLowerCase() === baseFolder.toLowerCase()) {
                    parts.splice(0, 1);
                }
                return parts.join('/');
            }
        }
        return fileName;
    }

    private static extractFileBaseName(fileName: string): string {
        if (!Utils.isNullOrUndefined(fileName)) {
            const parts = fileName.split('/');
            if (parts.length === 3) {
                return parts[2];
            }
        }

        return fileName;
    }

    private static createFileName(category: string, file: string): string {
        return `${baseFolder}/${category}/${file}`;
    }

    private static readDefinition(): Promise<ForceToolDefinition> {
        return readLocalJsonFile<FileDefinition>(UriHelper.setTimeStamp(fileDefinition))
            .catch(err => {
                if (DEBUG) {
                    console.warn(`Read force tool definition from file '${fileDefinition}' failed`, err);
                }

                return null;
            })
            .then(fileDefinition => {
                const result: ForceToolDefinition = {
                    Categories: []
                };

                if (!Utils.isNullOrUndefined(fileDefinition) && !Utils.isNullOrUndefined(fileDefinition.Categories)) {
                    for (const categoryName in fileDefinition.Categories) {
                        if (fileDefinition.Categories.hasOwnProperty(categoryName)) {
                            const setCategory: SetCategory = {
                                Name: categoryName,
                                Sets: []
                            };

                            const categoryDefinition = fileDefinition.Categories[categoryName] as FileCategoryDefinition;

                            for (const setName in categoryDefinition) {
                                if (categoryDefinition.hasOwnProperty(setName)) {
                                    const setDefinition = categoryDefinition[setName] as SetDefinition;
                                    setDefinition.Name = setName;
                                    //setDefinition.File = `${baseFolder}/${categoryName}/${setDefinition.File}`;
                                    setDefinition.File = ForceToolApi.createFileName(categoryName, setDefinition.File);

                                    setCategory.Sets.push(setDefinition);
                                }
                            }

                            result.Categories.push(setCategory);
                        }
                    }
                }

                return result;
            });
    }
}

function readLocalJsonFile<T>(file: string, throwError?: boolean): Promise<T> {
    return FileUtils.readLocalJsonFile(file, throwError) as Promise<T>;
}


// WEBPACK FOOTER //
// ./src/ForceToolApi.ts